// Пакет 7b, п.23: согласование кампаний партнёров — чистая модель (без React и сети).
// Серверная часть — go/retention/p7b_approval.go. Здесь только то, что нужно экранам: типы ответов, подписи,
// подсветка нарушений правил бренда в тексте и выбор действий по правам.
import { normalizeStepPromo } from "./promo";
import type { MarketingBootstrap, MarketingCampaign, MarketingStep } from "./types";

/** Статус кампании «на согласовании» (Go: apprStatus). */
export const PENDING_APPROVAL = "pending_approval";

export function isPendingApproval(status: string | null | undefined): boolean {
  return status === PENDING_APPROVAL;
}

/** Состояние согласования кампании: pending — ждёт УК, approved — согласована, returned — вернули черновиком. */
export interface CampaignApproval {
  state: "pending" | "approved" | "returned";
  requestedAt?: string | null;
  /** Кто отправил (имя пользователя партнёра). */
  requestedBy?: string;
  decidedAt?: string | null;
  /** Комментарий УК к возврату; живёт до повторной отправки. */
  comment?: string;
}

/** Нарушение правил бренда в тексте кампании (подсветка, не автоотказ). */
export interface ApprovalViolation {
  kind: "discount" | "stopWord";
  channel: "sms" | "push" | "email" | string;
  step: string;
  field: string;
  value?: number;
  word?: string;
  snippet: string;
  message: string;
}

/** Правила бренда и выключатель из «Настроек» (GET /api/marketing/approval/settings). */
export interface ApprovalRules {
  enabled: boolean;
  /** 0 — предел не задан. */
  maxDiscountPercent: number;
  stopWords: string[];
  /** Правит только администратор УК с правами на каналы и управление. */
  editable: boolean;
}

/**
 * Правила из ответа сервера без null. Сервер до 21.10.2026 отдавал пустые стоп-слова как null, и «Настройки»
 * падали на stopWords.join — экран целиком показывал «Не удалось открыть раздел».
 */
export function normalizeApprovalRules(rules: ApprovalRules): ApprovalRules {
  return {
    ...rules,
    enabled: rules.enabled === true,
    maxDiscountPercent: Number(rules.maxDiscountPercent) || 0,
    stopWords: Array.isArray(rules.stopWords) ? rules.stopWords : [],
    editable: rules.editable === true,
  };
}

export interface ApprovalQueueItem extends MarketingCampaign {
  companyName?: string;
  approval?: CampaignApproval;
  violations: ApprovalViolation[];
}

export interface ApprovalQueue {
  items: ApprovalQueueItem[];
  total: number;
  /** Решать (согласовать/вернуть) может только администратор УК с правом запуска. */
  canDecide: boolean;
  rules: ApprovalRules;
}

/** Признак из bootstrap: согласование включено; required — запуск этого пользователя пойдёт через него. */
export interface ApprovalBootstrap {
  enabled: boolean;
  required: boolean;
  canDecide: boolean;
}

export function approvalOf(bootstrap: Pick<MarketingBootstrap, "approval">): ApprovalBootstrap {
  return bootstrap.approval ?? { enabled: false, required: false, canDecide: false };
}

/**
 * Запуск ЭТОГО пользователя ставит кампанию партнёра в очередь УК (первый запуск черновика, когда выключатель включён и
 * права решать нет). Это относится и к партнёру, и к сотруднику сети без права решать.
 */
export function launchNeedsApproval(
  bootstrap: Pick<MarketingBootstrap, "approval">,
  status: string | null | undefined,
): boolean {
  return approvalOf(bootstrap).required && (status ?? "draft") === "draft";
}

/** Кнопки решения в карточке: только администратору УК и только для кампании на согласовании. */
export function canDecideApproval(
  bootstrap: Pick<MarketingBootstrap, "approval">,
  status: string | null | undefined,
): boolean {
  return approvalOf(bootstrap).canDecide && isPendingApproval(status);
}

/** Комментарий возврата для баннера черновика; нет — null. Показываем, пока кампания остаётся черновиком. */
export function returnedComment(
  campaign: Pick<MarketingCampaign, "status" | "approval"> | null | undefined,
): string | null {
  if (!campaign || campaign.status !== "draft") return null;
  const approval = campaign.approval;
  return approval?.state === "returned" ? approval.comment?.trim() || "Без комментария" : null;
}

/** Подписи действий согласования для тостов и ошибок. */
export const APPROVAL_TOASTS = {
  submitted: "Кампания отправлена на согласование",
  approved: "Кампания согласована и запущена",
  returned: "Кампания возвращена партнёру с комментарием",
  withdrawn: "Кампания отозвана с согласования — снова черновик",
} as const;

/** Статус кампании после запуска: «запущена» или «отправлена на согласование». */
export function launchResultText(status: string | null | undefined): string {
  return isPendingApproval(status) ? APPROVAL_TOASTS.submitted : "Кампания запущена";
}

// ───────────── подсветка нарушений в тексте ─────────────

/** Сворачивание регистра и «ё»→«е» — как Go apprFold; длина строки не меняется. */
export function foldText(text: string): string {
  return text.toLowerCase().replace(/ё/g, "е");
}

const escapeRe = (word: string) => word.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

/**
 * Процент в тексте: «скидка 15%», «20 %», «20\u00a0%» (неразрывный пробел), «20％» (полноширинный знак), «30 процентов».
 * Выражение то же, что на сервере (Go: apprPercentRe).
 */
const PERCENT = /(\d{1,3})(?:[.,]\d+)?[\s\p{Zs}]*(?:%|％|проц)/gu;

export interface TextPart {
  text: string;
  /** Часть текста нарушает правило бренда. */
  flagged: boolean;
}

/**
 * Режет текст на куски и помечает нарушения: скидка выше предела и стоп-слова (с начала слова, без учёта регистра и ё/е).
 * Это только подсветка для глаза; список нарушений для решения отдаёт сервер (violations).
 */
export function highlightText(
  text: string,
  rules: Pick<ApprovalRules, "maxDiscountPercent" | "stopWords">,
): TextPart[] {
  if (!text) return [];
  const folded = foldText(text);
  const marks: Array<[number, number]> = [];
  if (rules.maxDiscountPercent > 0) {
    for (const match of folded.matchAll(PERCENT)) {
      const value = Number(match[1]);
      if (value > rules.maxDiscountPercent && value <= 100)
        marks.push([match.index ?? 0, (match.index ?? 0) + match[0].length]);
    }
  }
  const words = [...new Set(rules.stopWords.map((word) => foldText(word.trim())).filter(Boolean))];
  if (words.length) {
    // Слово ищется с начала слова (после нелитеры или начала строки); подсвечивается слово целиком.
    const re = new RegExp(`(^|[^\\p{L}\\p{N}])((?:${words.map(escapeRe).join("|")})[\\p{L}\\p{N}]*)`, "gu");
    for (const match of folded.matchAll(re)) {
      const from = (match.index ?? 0) + match[1].length;
      marks.push([from, from + match[2].length]);
    }
  }
  if (!marks.length) return [{ text, flagged: false }];
  marks.sort((a, b) => a[0] - b[0]);
  const merged: Array<[number, number]> = [];
  for (const mark of marks) {
    const last = merged[merged.length - 1];
    if (last && mark[0] <= last[1]) last[1] = Math.max(last[1], mark[1]);
    else merged.push([mark[0], mark[1]]);
  }
  const parts: TextPart[] = [];
  let cursor = 0;
  for (const [from, to] of merged) {
    if (from > cursor) parts.push({ text: text.slice(cursor, from), flagged: false });
    parts.push({ text: text.slice(from, to), flagged: true });
    cursor = to;
  }
  if (cursor < text.length) parts.push({ text: text.slice(cursor), flagged: false });
  return parts;
}

/** Тексты сообщений кампании для карточки согласования: «Сообщение 1 · SMS» и поля текста. */
export interface ApprovalMessageView {
  label: string;
  channel: string;
  lines: Array<{ field: string; text: string }>;
}

const CHANNEL_NAME: Record<string, string> = { sms: "SMS", push: "Пуш", email: "Письмо" };

export function approvalMessages(
  campaign: Pick<MarketingCampaign, "steps">,
): ApprovalMessageView[] {
  const out: ApprovalMessageView[] = [];
  let index = 0;
  for (const step of campaign.steps ?? []) {
    if (step.type && step.type !== "message") continue;
    index += 1;
    const lines: ApprovalMessageView["lines"] = [];
    const add = (field: string, text: string | undefined) => {
      if (text && text.trim()) lines.push({ field, text });
    };
    if (step.channel === "sms") add("Текст SMS", step.smsText);
    else if (step.channel === "push") {
      add("Заголовок", step.push?.title);
      add("Текст", step.push?.body);
    } else if (step.channel === "email") {
      add("Тема", step.email?.subject);
      add("Заголовок", step.email?.title);
      add("Текст", step.email?.body);
      for (const block of step.email?.blocks ?? []) {
        add("Блок", block.title);
        add("Блок", block.text);
        for (const span of block.spans ?? []) add("Блок", span.text);
      }
    }
    out.push({ label: `Сообщение ${index} · ${CHANNEL_NAME[step.channel] ?? step.channel}`, channel: step.channel, lines });
  }
  return out;
}

// ───────────── что ещё видит гость: ссылки, промокод, время ─────────────

export interface ApprovalLink {
  step: string;
  field: string;
  url: string;
}

const URL_IN_TEXT = /https?:\/\/[^\s<>"')]+/gi;

/** Только http(s): остальное в ссылку не превращаем. */
export function isWebUrl(value: string): boolean {
  return /^https?:\/\/[^\s]+$/i.test(value);
}

/**
 * Все ссылки в сообщениях кампании: кнопки писем, ссылки блоков и подвала, `push.link`, ссылки в `href` оформления и
 * адреса прямо в тексте SMS. Администратор видит, куда ведёт каждая, до согласования.
 */
export function approvalLinks(campaign: Pick<MarketingCampaign, "steps">): ApprovalLink[] {
  const out: ApprovalLink[] = [];
  const seen = new Set<string>();
  let index = 0;
  for (const step of campaign.steps ?? []) {
    if (step.type && step.type !== "message") continue;
    index += 1;
    const label = `Сообщение ${index} · ${CHANNEL_NAME[step.channel] ?? step.channel}`;
    const add = (field: string, url: string | undefined) => {
      const value = (url ?? "").trim();
      if (!value) return;
      const key = `${label}|${field}|${value}`;
      if (seen.has(key)) return;
      seen.add(key);
      out.push({ step: label, field, url: value });
    };
    const inText = (field: string, text: string | undefined) => {
      for (const match of (text ?? "").matchAll(URL_IN_TEXT)) add(field, match[0]);
    };
    if (step.channel === "sms") inText("Адрес в тексте SMS", step.smsText);
    else if (step.channel === "push") {
      add("Куда ведёт пуш", step.push?.link);
      inText("Адрес в тексте пуша", step.push?.body);
    } else if (step.channel === "email") {
      const e = step.email;
      add("Кнопка письма", e?.buttonUrl);
      for (const link of e?.footerLinks ?? []) add(`Ссылка внизу: ${link.label}`, link.url);
      for (const block of e?.blocks ?? []) {
        add(block.type === "button" ? `Кнопка «${block.buttonText ?? ""}»` : "Ссылка блока", block.url);
        for (const span of block.spans ?? []) add(`Ссылка в тексте: ${span.text}`, span.href);
        for (const item of block.items ?? []) add(`Блюдо: ${item.title}`, item.url);
      }
    }
  }
  return out;
}

export interface ApprovalPromo {
  step: string;
  /** Режим промокода шага простыми словами. */
  mode: string;
  /** Общий код или идентификатор серии — то, что стоит в шаге. */
  value: string;
  poolId: string;
}

/** Промокод и режим промокода каждого сообщения (старый общий код кампании читается как «общий»). */
export function approvalPromos(campaign: Pick<MarketingCampaign, "steps" | "promoCode">): ApprovalPromo[] {
  const out: ApprovalPromo[] = [];
  let index = 0;
  for (const raw of campaign.steps ?? []) {
    if (raw.type && raw.type !== "message") continue;
    index += 1;
    const step: MarketingStep = normalizeStepPromo(raw, campaign.promoCode ?? "");
    const label = `Сообщение ${index} · ${CHANNEL_NAME[step.channel] ?? step.channel}`;
    if (step.promoMode === "shared")
      out.push({ step: label, mode: "Один общий код для всех", value: step.promoCode ?? "", poolId: "" });
    else if (step.promoMode === "pool")
      out.push({ step: label, mode: "Уникальный код из серии", value: "", poolId: step.promoPoolId ?? "" });
    else out.push({ step: label, mode: "Без промокода", value: "", poolId: "" });
  }
  return out;
}

const SEND_TIME_LABEL: Record<string, string> = {
  fixed: "в выбранный час",
  last_order: "по времени последнего заказа гостя",
  habit: "в привычный час гостя",
  habit_day: "в привычный день недели и час гостя",
  asap: "сразу, как гость подошёл под условия (в разрешённое время)",
};

export interface ApprovalTiming {
  /** «Не раньше 22.10.2026, 12:00 (Москва)» или «Сразу после согласования». */
  start: string;
  /** «В выбранный час: 11:00 по местному времени города». */
  send: string;
  /** Начало уже в прошлом: после согласования кампания стартует сразу. */
  startsImmediately: boolean;
}

/** Когда стартует кампания и в какое время суток пишет гостю. now — для проверки «начало в прошлом». */
export function approvalTiming(
  campaign: Pick<MarketingCampaign, "scheduledAt" | "sendHour" | "sendTimeMode" | "sendOffsetMinutes" | "mode">,
  timezone = "Europe/Moscow",
  now: number = Date.now(),
): ApprovalTiming {
  const at = campaign.scheduledAt ? Date.parse(campaign.scheduledAt) : NaN;
  const dated = Number.isFinite(at);
  let start = "Сразу после согласования";
  if (dated) {
    try {
      start = `Не раньше ${new Date(at).toLocaleString("ru-RU", { timeZone: timezone, dateStyle: "short", timeStyle: "short" })} (${timezone})`;
    } catch {
      start = `Не раньше ${new Date(at).toISOString()}`;
    }
  }
  const mode = campaign.sendTimeMode ?? "fixed";
  const label = SEND_TIME_LABEL[mode] ?? SEND_TIME_LABEL.fixed;
  const send =
    mode === "fixed"
      ? `${label[0].toUpperCase()}${label.slice(1)}: ${campaign.sendHour}:00 по местному времени города`
      : `${label[0].toUpperCase()}${label.slice(1)}${mode === "last_order" || mode === "habit" || mode === "habit_day" ? `, сдвиг ${campaign.sendOffsetMinutes ?? 0} мин` : ""}`;
  return { start, send, startsImmediately: dated && at <= now };
}

/** Предупреждение, если начало кампании уже прошло: согласование запустит её немедленно. */
export const STARTS_NOW_WARNING =
  "Дата начала уже прошла — кампания стартует сразу после согласования. Если нужна другая дата, верните кампанию с комментарием.";

/**
 * Сообщения запущенной кампании на паузе закрыты для правки: при включённом согласовании менять их может только тот, кто вправе
 * решать (сервер отвечает 409, Go: marketingSaveLive). Название и лимит остаются доступными.
 */
export function messagesLockedByApproval(
  bootstrap: Pick<MarketingBootstrap, "approval">,
  record: { status: string } | null | undefined,
  scopeLocked: boolean,
): boolean {
  return approvalOf(bootstrap).required && !!record && scopeLocked && !isPendingApproval(record.status);
}

/** Сводка нарушений для заголовка карточки: «2 нарушения правил бренда» / «Правила бренда соблюдены». */
export function violationsSummary(violations: readonly ApprovalViolation[], rulesSet: boolean): string {
  if (!violations.length) return rulesSet ? "Правила бренда соблюдены" : "Правила бренда не заданы";
  const n = violations.length;
  const mod10 = n % 10;
  const mod100 = n % 100;
  const word = mod10 === 1 && mod100 !== 11 ? "нарушение" : mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14) ? "нарушения" : "нарушений";
  return `${n} ${word} правил бренда`;
}

/** Есть ли вообще правила бренда (для подписи «соблюдены»/«не заданы»). */
export function hasBrandRules(rules: Pick<ApprovalRules, "maxDiscountPercent" | "stopWords"> | null | undefined): boolean {
  return !!rules && (rules.maxDiscountPercent > 0 || rules.stopWords.length > 0);
}

/** Стоп-слова из многострочного поля: по строкам и запятым, без повторов (сервер приведёт регистр и «ё»). */
export function parseStopWords(raw: string): string[] {
  const out: string[] = [];
  const seen = new Set<string>();
  for (const part of raw.split(/[\n\r,;]+/)) {
    const word = part.trim();
    const key = foldText(word);
    if (!word || seen.has(key)) continue;
    seen.add(key);
    out.push(word);
  }
  return out;
}

export interface ApprovalSettingsDraft {
  enabled: boolean;
  maxDiscount: string;
  stopWords: string;
}

export function settingsToDraft(rules: Pick<ApprovalRules, "enabled" | "maxDiscountPercent" | "stopWords">): ApprovalSettingsDraft {
  return {
    enabled: rules.enabled,
    maxDiscount: rules.maxDiscountPercent > 0 ? String(rules.maxDiscountPercent) : "",
    stopWords: rules.stopWords.join("\n"),
  };
}

/** Ошибки полей настроек простыми словами; пусто — можно сохранять. */
export function settingsErrors(draft: ApprovalSettingsDraft): { maxDiscount?: string; stopWords?: string } {
  const errors: { maxDiscount?: string; stopWords?: string } = {};
  const raw = draft.maxDiscount.trim();
  if (raw !== "" && !(/^\d{1,3}$/.test(raw) && Number(raw) >= 1 && Number(raw) <= 100))
    errors.maxDiscount = "Целое число от 1 до 100 процентов или пусто — без ограничения.";
  const words = parseStopWords(draft.stopWords);
  if (words.length > 50) errors.stopWords = "Стоп-слов не больше 50.";
  else if (words.some((word) => word.length < 2 || word.length > 40))
    errors.stopWords = "Каждое стоп-слово — от 2 до 40 символов.";
  return errors;
}

export function settingsPayload(draft: ApprovalSettingsDraft): {
  enabled: boolean;
  maxDiscountPercent: number;
  stopWords: string[];
} {
  const raw = draft.maxDiscount.trim();
  return {
    enabled: draft.enabled,
    maxDiscountPercent: raw === "" ? 0 : Number(raw),
    stopWords: parseStopWords(draft.stopWords),
  };
}
